/**
 *  vuex 的用户相关信息模块
 */
import { localStoragePrefix } from "@/scripts/utils";
import Vue from 'vue'
const user = {
  namespaced: true,
  state: () => {
    return {
      account: "",
      info: {},
      roleList: [],
      permission: {},
      answerSheetAliToken: '',
      funcCodes: [],
      permissionEnabled: true,
      userScope: { grades: [], subjects: [], period_scope: [], enrollment_years: [] }
    };
  },
  getters: {
    isSuperAdmin: (state) => state.info.userName === "阅卷管理",
    isScoringTeacher: (state) => (state.info ? parseFloat(state.info.userRole) === 4 : false),
    hasPermission: (state) => (funcCode) => state.funcCodes.includes(funcCode),
    hasAnyPermission: (state) => (codes) => codes.some(code => state.funcCodes.includes(code)),
    userId: (state) => state.permission.user_id || "",
    sessionId: (state) => state.info.sessionId || "",
    userName: (state) => state.info.userName || "",
    userSchoolName(state, getters) {
      return state.info.userSchoolName || "";
    },
    userSchoolId(state, getters) {
      return state.info.userSchoolId;
    },
    userSchoolStage(state, getters) {
      return state.info.userStage;
    },
    currentRole: (state) => {
      // 目前仅区分四个角色，1.学校管理员 2.市教研室老师 3.镇教办老师 4.阅卷老师。
      let userRole = parseFloat(state.info.userRole);
      if (userRole === 4) return "scoring_teacher"; // 阅卷老师
      const { level } = state.permission.permission;
      switch (level) {
        case 4:
          return "school_manager"; // 学校管理员
        case 3:
          return "town_edu_teacher"; // 镇教办老师
        case 2:
          return "city_edu_research"; // 市教研院
        case 1:
          return "city_edu_research"; // 省、市教研院
        default:
          return "unknown"; // 其他未定义的角色
      }
    },
    // 联考的业务逻辑，已弃用
    isTeacher: (state, getters) => {
      // return getters.currentRole === "school_manager";
      return true;
    },
    isManage: (state, getters) => {
      return false;
      return ["town_edu_teacher", "city_edu_research"].includes(getters.currentRole);
    },
    isPermissionEnabled: (state) => state.permissionEnabled,
    permissionIds(state, getters) {
      const userPermission = state.permission || {};
      const p = userPermission.permission || {};
      const { level, province_list, city_list, county_list, school_list } = p;
      switch (level) {
        case 1:
          return { province_ids: JSON.stringify(province_list.map((e) => e.F_id)) };
        case 2:
          return { city_ids: JSON.stringify(city_list.map((e) => e.F_id)) };
        case 3:
          return { county_ids: JSON.stringify(county_list.map((e) => e.F_id)) };
        // 单校版业务逻辑改了，不再传多校id，只传单校学校的 id
        case 4:
          return { school_ids: JSON.stringify([getters.userSchoolId]) };
      }
    },
    permissionSubjectList(state) {
      return (state.permission.permission && state.permission.permission.subject_list) || [];
    },
    permissionSchoolList(state) {
      return (state.permission.permission && state.permission.permission.school_list) || [];
    },
  },
  mutations: {
    SET_ACCOUNT(state, payload) {
      state.account = payload.account;
    },
    SET_USER_INFO(state, payload) {
      state.info = payload.userInfo;
    },
    SET_USER_ROLE_LIST(state, payload) {
      // 根据 scope_level 放开数据范围限制，替代旧有硬编码角色名
      const { value } = payload;
      value.forEach(item => {
        const level = item.scope_level;
        if (level === 'school' || level === 'global') {
          item.permission.grade_list = [];
          item.permission.class_list = [];
          item.permission.subject_list = [];
        } else if (level === 'grade') {
          item.permission.class_list = [];
          item.permission.subject_list = [];
        } else if (level === 'subject' || level === 'subject_grade') {
          item.permission.class_list = [];
        } else if (level === 'class') {
          item.permission.subject_list = [];
        }
        // 'subject_class' 和空值：保留所有列表不做清除
      })
      state.roleList = payload.value;
    },
    CHANGE_CURR_USER_ROLE(state, payload) {
      state.permission = payload.value;
    },
    SET_USER_SCHOOL(state, payload) {
      const { F_id, F_name } = payload;
      state.info.userSchoolId = F_id;
      state.info.userSchoolName = F_name;
      sessionStorage.setItem(`${localStoragePrefix}user_school_id`, F_id);
      sessionStorage.setItem(`${localStoragePrefix}user_school_name`, F_name);
    },
    SET_ANSWER_SHEET_ALI_TOKEN(state, payload) {
      state.answerSheetAliToken = payload.token;
    },
    SET_FUNC_CODES(state, payload) {
      state.funcCodes = payload.value || [];
    },
    SET_PERMISSION_ENABLED(state, payload) {
      state.permissionEnabled = payload.value;
    },
    SET_USER_SCOPE(state, payload) {
      state.userScope = payload.value || { grades: [], subjects: [], period_scope: [], enrollment_years: [] };
    },
  },
  actions: {
    async getAliToken({ state, commit }, payload) {
      const now = new Date().getTime() / 1000;
      if (state.answerSheetAliToken && now <= state.answerSheetAliToken.Expiration - 300) return;
      const res = await Vue.prototype.$serve.getStsToken();
      if (res.body.F_responseNo === 10000) {
        commit('SET_ANSWER_SHEET_ALI_TOKEN', { token: res.body.F_data })
      }
    },
    async fetchFuncCodes({ commit, getters }) {
      try {
        const res = await Vue.prototype.$serve.getFuncCodes(getters.userId);
        if (res.body && res.body.status_code === 200) {
          commit('SET_FUNC_CODES', { value: res.body.data });
          // permission_enabled 为后端权限模块开关状态，关闭时前端也应跳过数据过滤
          if (res.body.permission_enabled !== undefined) {
            commit('SET_PERMISSION_ENABLED', { value: res.body.permission_enabled });
          }
        }
      } catch (e) {
        console.warn('[permission] func_codes fetch failed, fallback to role-based control');
      }
    },
    async fetchUserScope({ commit, getters }) {
      try {
        const res = await Vue.prototype.$serve.getUserScope(getters.userId, getters.userSchoolId);
        if (res.body && res.body.data) {
          commit('SET_USER_SCOPE', { value: res.body.data });
        }
      } catch (e) {
        console.warn('[permission] scope fetch failed');
      }
    }
  },
};

export default user;
